import config from "@/config";
import { fetchBundledLocalesList } from "./bundled-locales-list";
import {
  isPrimaryLanguage,
  PRIMARY_LANGUAGE_LOCALES,
  withPrimaryLanguage,
} from "./primary-language";
import {
  fetchTranslationManifest,
  resourcePathsFromManifest,
  type TranslationManifest,
} from "./translation-manifest";
import { formatTranslationsVersionLine } from "./translations-version";

const BUNDLED_LOCALES_FETCH_TIMEOUT_MS = 3000;

// Passed into i18next's own ns/defaultNS init options (see index.ts) so the
// manifest lookup and i18next's namespace config can't drift apart.
export const DEFAULT_NAMESPACE = "translation";

// Populated once when getTranslationLocales() fetches the manifest at startup,
// then read synchronously by getCdnTranslationPath (i18next's loadPath is sync).
// Keyed by namespace first since the manifest may cover more than one.
let cdnResourcePaths: Record<string, Record<string, string>> | null = null;

/** Manifest `path` values include the blob container prefix (`translations/…`). */
function cdnUrlForManifestPath(manifestPath: string): string {
  const relativePath = manifestPath.startsWith("translations/")
    ? manifestPath.slice("translations/".length)
    : manifestPath;
  return `${config.translations.cdnBaseUrl}/${relativePath}`;
}

function bundledFilename(namespace: string = DEFAULT_NAMESPACE): string {
  return `${namespace}.json`;
}

/** Published layout when manifest paths are missing or the fetch failed. */
function defaultManifestPath(
  locale: string,
  namespace: string = DEFAULT_NAMESPACE
): string {
  const line = formatTranslationsVersionLine();
  return `translations/ui/${line}/${locale}/${bundledFilename(namespace)}`;
}

function buildDefaultCdnResourcePaths(
  locales: readonly string[]
): Record<string, Record<string, string>> {
  const foreignLocales = locales.filter((locale) => !isPrimaryLanguage(locale));
  if (foreignLocales.length === 0) {
    return {};
  }

  return {
    [DEFAULT_NAMESPACE]: Object.fromEntries(
      foreignLocales.map((locale) => [
        locale,
        defaultManifestPath(locale, DEFAULT_NAMESPACE),
      ])
    ),
  };
}

function cacheDefaultCdnResourcePaths(locales: readonly string[]): void {
  cdnResourcePaths = buildDefaultCdnResourcePaths(locales);
}

async function readBundledLocalesList(): Promise<string[]> {
  return fetchBundledLocalesList(BUNDLED_LOCALES_FETCH_TIMEOUT_MS);
}

function cacheCdnResourcePathsFromManifest(
  manifest: TranslationManifest
): Record<string, Record<string, string>> | null {
  const resourcePaths = resourcePathsFromManifest(manifest);
  if (resourcePaths === null) {
    cdnResourcePaths = null;
    return null;
  }

  cdnResourcePaths = resourcePaths;
  return resourcePaths;
}

function localesWithCdnTranslationPaths(
  resourcePaths: Record<string, Record<string, string>>
): string[] {
  const localePaths = resourcePaths[DEFAULT_NAMESPACE];
  if (localePaths === undefined) {
    return [];
  }

  return Object.keys(localePaths);
}

export function getBundledTranslationPath(
  locale: string,
  namespace: string = DEFAULT_NAMESPACE
): string {
  return `/locales/${locale}/${bundledFilename(namespace)}`;
}

export function getCdnTranslationPath(
  locale: string,
  namespace: string = DEFAULT_NAMESPACE,
  resourcePaths: Record<
    string,
    Record<string, string>
  > | null = cdnResourcePaths
): string | null {
  if (isPrimaryLanguage(locale)) {
    return null;
  }

  if (resourcePaths === null) {
    return null;
  }

  const manifestPath = resourcePaths[namespace]?.[locale];
  if (manifestPath !== undefined) {
    return cdnUrlForManifestPath(manifestPath);
  }

  if (resourcePaths[DEFAULT_NAMESPACE]?.[locale] !== undefined) {
    return cdnUrlForManifestPath(defaultManifestPath(locale, namespace));
  }

  return null;
}

export async function getTranslationLocales(): Promise<string[]> {
  cdnResourcePaths = null;

  if (!config.translations.cdnEnabled) {
    return [...PRIMARY_LANGUAGE_LOCALES];
  }

  const bundledLocales = await readBundledLocalesList();
  const manifest = await fetchTranslationManifest();

  if (manifest === null) {
    cacheDefaultCdnResourcePaths(bundledLocales);
    return bundledLocales;
  }

  const resourcePaths = cacheCdnResourcePathsFromManifest(manifest);
  if (resourcePaths === null) {
    cacheDefaultCdnResourcePaths(bundledLocales);
    return bundledLocales;
  }

  const cdnLocales = localesWithCdnTranslationPaths(resourcePaths);
  if (cdnLocales.length === 0) {
    cacheDefaultCdnResourcePaths(bundledLocales);
    return bundledLocales;
  }

  return withPrimaryLanguage(cdnLocales);
}
